// src/pages/sessions/components/LiveSession/Chat/PersonalChatInSession.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  XMarkIcon, 
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  PaperClipIcon,
  DocumentTextIcon,
  LinkIcon,
  XCircleIcon,
  ArrowUpTrayIcon,
  ArrowUpIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { downloadFile } from '@/utils/downloadFile';
import { toast } from 'sonner';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useSoundEffects } from '@/hooks/useSoundEffects';
import { 
  getChatContacts, 
  getChatMessages,
  sendChatMessage,
  markMessagesAsRead,
  uploadChatAttachment,
  deleteChatAttachment,
  getOneDriveMetadata,
  getGoogleDriveMetadata
} from '@/services/api';

// Cleanup tracker for PersonalChatInSession
class ChatCleanupTracker {
  constructor() {
    this.timeouts = new Set();
    this.intervals = new Set();
    this.handlers = new Set();
    this.abortControllers = new Set();
    this.disposed = false;
  }

  setTimeout(fn, delay) {
    if (this.disposed) return null;
    const id = setTimeout(() => {
      this.timeouts.delete(id);
      if (!this.disposed) fn();
    }, delay);
    this.timeouts.add(id);
    return id;
  }

  setInterval(fn, delay) {
    if (this.disposed) return null;
    const id = setInterval(() => {
      if (!this.disposed) fn();
    }, delay);
    this.intervals.add(id);
    return id;
  }

  addHandler(handler) {
    if (this.disposed) return;
    this.handlers.add(handler);
  }

  createAbortController() {
    if (this.disposed) return null;
    const controller = new AbortController();
    this.abortControllers.add(controller);
    return controller;
  }

  cleanup() {
    this.disposed = true;
    this.timeouts.forEach(id => clearTimeout(id));
    this.timeouts.clear();
    this.intervals.forEach(id => clearInterval(id));
    this.intervals.clear();
    this.abortControllers.forEach(controller => {
      try {
        controller.abort();
      } catch (e) {}
    });
    this.abortControllers.clear();
    this.handlers.clear();
  }
}

// Helper function to decode HTML entities
function decodeHtmlEntities(text) {
  const textArea = document.createElement('textarea');
  textArea.innerHTML = text;
  const result = textArea.value;
  textArea.remove();
  return result;
}

// Smart date formatting with i18n
function formatDateSeparator(timestamp, t) {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const yesterdayDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
  
  if (messageDate.getTime() === todayDate.getTime()) {
    return t('personalChat.date.today');
  } else if (messageDate.getTime() === yesterdayDate.getTime()) {
    return t('personalChat.date.yesterday');
  } else {
    const diffTime = todayDate.getTime() - messageDate.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    
    if (diffDays < 7) {
      return date.toLocaleDateString(t('common.locale'), { weekday: 'long' });
    }
    
    return date.toLocaleDateString(t('common.locale'), { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  }
}

function getDateKey(timestamp) {
  const date = new Date(timestamp);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

// SmartLink component with cleanup
const SmartLink = ({ url, isYourMessage, type = 'generic' }) => {
  const { t } = useTranslation();
  const [displayName, setDisplayName] = useState(() => {
    if (type === 'onedrive') return t('personalChat.file.oneDrive');
    if (type === 'google-doc') return t('personalChat.file.googleDoc');
    if (type === 'google-sheet') return t('personalChat.file.googleSheet');
    if (type === 'google-presentation') return t('personalChat.file.googlePresentation');
    if (type === 'google-drive') return t('personalChat.file.googleDrive');
    if (type === 'generic') return url.length > 100 ? url.substring(0, 97) + '...' : url;
    return url;
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const mountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  
  useEffect(() => {
    mountedRef.current = true;
    
    if (type === 'onedrive' && (url.includes('1drv.ms') || url.includes('1drv.com'))) {
      setIsLoading(true);
      abortControllerRef.current = new AbortController();
      
      getOneDriveMetadata(url, { signal: abortControllerRef.current.signal })
        .then(response => {
          if (!mountedRef.current) return;
          if (response.success && response.data) {
            if (response.data.requiresAuth) {
              setDisplayName(`${t('personalChat.file.private')} ${response.data.filename || t('personalChat.file.oneDrive')}`);
            } else {
              setDisplayName(response.data.filename || t('personalChat.file.oneDrive'));
            }
          }
        })
        .catch(err => {
          if (err.name !== 'AbortError' && mountedRef.current) {
            // Keep default name on error
          }
        })
        .finally(() => {
          if (mountedRef.current) {
            setIsLoading(false);
          }
        });
    }
    
    if (type.startsWith('google-') && (url.includes('docs.google.com') || url.includes('drive.google.com'))) {
      setIsLoading(true);
      abortControllerRef.current = new AbortController();
      
      getGoogleDriveMetadata(url, { signal: abortControllerRef.current.signal })
        .then(response => {
          if (!mountedRef.current) return;
          if (response.success && response.data?.filename) {
            setDisplayName(response.data.filename);
          }
        })
        .catch(err => {
          if (err.name !== 'AbortError' && mountedRef.current) {
            // Keep the default name on error
          }
        })
        .finally(() => {
          if (mountedRef.current) {
            setIsLoading(false);
          }
        });
    }
    
    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [url, type, t]);
  
  const linkColorClass = isYourMessage 
    ? 'text-cyan-100 hover:text-white' 
    : 'text-blue-400 hover:text-blue-300';
  
  if (type === 'onedrive') {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-2 ${linkColorClass} hover:underline transition-all`}
        onClick={(e) => e.stopPropagation()}
      >
        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"/>
        </svg>
        <span className={`font-medium ${isLoading ? 'opacity-60' : ''}`}>
          {displayName}
        </span>
        {isLoading && (
          <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
        )}
      </a>
    );
  }
  
  if (type.startsWith('google-')) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-2 ${linkColorClass} hover:underline transition-all`}
        onClick={(e) => e.stopPropagation()}
      >
        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
        </svg>
        <span className={`font-medium ${isLoading ? 'opacity-60' : ''}`}>
          {displayName}
        </span>
        {isLoading && (
          <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
        )}
      </a>
    );
  }
  
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1 ${linkColorClass} underline break-all`}
      onClick={(e) => e.stopPropagation()}
    >
      <LinkIcon className="w-4 h-4 flex-shrink-0" />
      <span>{displayName}</span>
    </a>
  );
};

const MemoizedSmartLink = React.memo(SmartLink);

function formatMessageWithLinks(text, isYourMessage = false) {
  if (!text) return text;
  
  const decodedText = decodeHtmlEntities(text);
  const urlRegex = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
  const parts = decodedText.split(urlRegex);
  
  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      const lowerUrl = part.toLowerCase();
      
      if (lowerUrl.includes('1drv.ms') || 
          lowerUrl.includes('1drv.com') ||
          lowerUrl.includes('onedrive.live.com') ||
          lowerUrl.includes('sharepoint.com')) {
        return <MemoizedSmartLink key={index} url={part} isYourMessage={isYourMessage} type="onedrive" />;
      }
      
      if (lowerUrl.includes('docs.google.com')) {
        if (lowerUrl.includes('/document/')) {
          return <MemoizedSmartLink key={index} url={part} isYourMessage={isYourMessage} type="google-doc" />;
        }
        if (lowerUrl.includes('/spreadsheets/')) {
          return <MemoizedSmartLink key={index} url={part} isYourMessage={isYourMessage} type="google-sheet" />;
        }
        if (lowerUrl.includes('/presentation/')) {
          return <MemoizedSmartLink key={index} url={part} isYourMessage={isYourMessage} type="google-presentation" />;
        }
      }
      
      if (lowerUrl.includes('drive.google.com')) {
        return <MemoizedSmartLink key={index} url={part} isYourMessage={isYourMessage} type="google-drive" />;
      }
      
      return <MemoizedSmartLink key={index} url={part} isYourMessage={isYourMessage} type="generic" />;
    }
    
    return <span key={index}>{part}</span>;
  });
}

function formatTime24h(timestamp) {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

// Main component
function PersonalChatInSession({ isOpen, onClose, currentUser, onUnreadCountChange, sessionId, isActive }) {
  const { t } = useTranslation();
  const cleanupTrackerRef = useRef(new ChatCleanupTracker());
  const cleanup = cleanupTrackerRef.current;
  
  const { sendMessage: wsSendMessage, addMessageHandler, isConnected } = useWebSocket();
  const { playDirectMessage } = useSoundEffects();
  
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [showUserList, setShowUserList] = useState(true);
  
  const [attachedFile, setAttachedFile] = useState(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  const [oldestMessageId, setOldestMessageId] = useState(null);

  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [contactsError, setContactsError] = useState('');
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [sendMessageError, setSendMessageError] = useState('');

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const textareaRef = useRef(null);
  
  const MESSAGE_CHAR_LIMIT = 1000;
  const MAX_FILE_SIZE = 20 * 1024 * 1024;
  const MESSAGES_PER_PAGE = 50;
  
  const mountedRef = useRef(true);
  
  const hasLoadedContactsRef = useRef(false);
  const isOpenRef = useRef(isOpen);
  const currentUserIdRef = useRef(currentUser?.id || currentUser?.userId);
  const selectedUserRef = useRef(selectedUser);
  const showUserListRef = useRef(showUserList);
  const isActiveRef = useRef(isActive);
  const isLoadingMoreRef = useRef(false);
  const isAtBottomRef = useRef(true);
  const contactsRef = useRef(contacts);
  
  const wsUnsubscribersRef = useRef([]);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    currentUserIdRef.current = currentUser?.id || currentUser?.userId;
  }, [currentUser]);

  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  useEffect(() => {
    showUserListRef.current = showUserList;
  }, [showUserList]);

  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  useEffect(() => {
    contactsRef.current = contacts;
  }, [contacts]);

  useEffect(() => {
    const totalUnread = contacts.reduce((sum, contact) => sum + (contact.unread || 0), 0);
    
    if (onUnreadCountChange) {
      onUnreadCountChange(totalUnread);
    }
  }, [contacts, onUnreadCountChange]);

  useEffect(() => {
    if (!isOpen || showUserList || !selectedUser) return;

    const preventBrowserDefault = (e) => {
      e.preventDefault();
    };

    document.addEventListener('dragover', preventBrowserDefault, false);
    document.addEventListener('drop', preventBrowserDefault, false);

    return () => {
      document.removeEventListener('dragover', preventBrowserDefault, false);
      document.removeEventListener('drop', preventBrowserDefault, false);
    };
  }, [isOpen, showUserList, selectedUser]);

  useEffect(() => {
    const dropZone = dropZoneRef.current;
    
    if (!dropZone || !selectedUser || showUserList) return;

    const handleNativeDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer?.types.includes('Files')) {
        setIsDragging(true);
      }
    };

    const handleNativeDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'copy';
    };

    const handleNativeDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!dropZone.contains(e.relatedTarget)) {
        setIsDragging(false);
      }
    };

    const handleNativeDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      
      const files = e.dataTransfer.files;
      if (!files || files.length === 0) return;
      
      const file = files[0];
      
      if (file.size > MAX_FILE_SIZE) {
        const sizeMB = (file.size / 1024 / 1024).toFixed(2);
        setSendMessageError(t('personalChat.error.fileTooLargeDetailed', { size: sizeMB }));
        toast.error(t('personalChat.error.fileTooLarge'));
        return;
      }
      
      setAttachedFile(file);
      setSendMessageError('');
      toast.success(t('personalChat.file.ready', { name: file.name }));
    };

    dropZone.addEventListener('dragenter', handleNativeDragEnter, true);
    dropZone.addEventListener('dragover', handleNativeDragOver, true);
    dropZone.addEventListener('dragleave', handleNativeDragLeave, true);
    dropZone.addEventListener('drop', handleNativeDrop, true);

    return () => {
      dropZone.removeEventListener('dragenter', handleNativeDragEnter, true);
      dropZone.removeEventListener('dragover', handleNativeDragOver, true);
      dropZone.removeEventListener('dragleave', handleNativeDragLeave, true);
      dropZone.removeEventListener('drop', handleNativeDrop, true);
    };
  }, [selectedUser, showUserList, MAX_FILE_SIZE, t]);

  const scrollToBottom = useCallback((force = false) => {
    if (!mountedRef.current) return;
    if (!force && !isAtBottomRef.current) return;
    // column-reverse: visual bottom = scrollTop 0
    requestAnimationFrame(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = 0;
      }
    });
  }, []);

  useEffect(() => {
    if (chatMessages.length > 0 && !isLoadingMoreRef.current) {
      scrollToBottom(true);
    }
  }, [chatMessages.length, scrollToBottom]);

  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current) return;
    
    const { scrollTop } = messagesContainerRef.current;
    
    // column-reverse: scrollTop 0 = visual bottom, negative = scrolled up
    isAtBottomRef.current = Math.abs(scrollTop) < 50;
    
    if (isLoadingMoreMessages || !hasMoreMessages) return;
    
    if (scrollTop < 50) {
      loadMoreMessages();
    }
  }, [isLoadingMoreMessages, hasMoreMessages]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const markMessagesAsReadAndSync = useCallback(async (userId) => {
    if (!mountedRef.current) return;
    
    try {
      await markMessagesAsRead(userId);
      
      if (!mountedRef.current) return;
      
      setContacts(prev => prev.map(contact => 
        String(contact.id) === String(userId) 
          ? { ...contact, unread: 0 }
          : contact
      ));
      
      setFilteredContacts(prev => prev.map(contact => 
        String(contact.id) === String(userId) 
          ? { ...contact, unread: 0 }
          : contact
      ));
      
      if (wsSendMessage && isConnected && mountedRef.current) {
        wsSendMessage({
          type: 'mark_read',
          userId: userId,
          timestamp: new Date().toISOString()
        });
      }
      
    } catch (err) {
      if (mountedRef.current) {
        console.error('Failed to mark as read:', err);
      }
    }
  }, [wsSendMessage, isConnected]);

  useEffect(() => {
    if (!isConnected) return;
    
    const handleChatMessage = async (data) => {
      if (!mountedRef.current) return;
      
      const senderId = data.senderId || data.sender_id || data.sender_user_id;
      const receiverId = data.receiverId || data.receiver_id || data.receiver_user_id;
      const currentUserId = currentUserIdRef.current;
      
      if (String(senderId) !== String(currentUserId) && String(receiverId) !== String(currentUserId)) {
        return;
      }
      
      const isIncomingMessage = String(senderId) !== String(currentUserId);
      const relevantUserId = isIncomingMessage ? senderId : receiverId;
      
      // Play notification sound for incoming personal messages
      if (isIncomingMessage) {
        playDirectMessage();
      }
      
      const isViewingThisConversation = !showUserListRef.current && 
                                        selectedUserRef.current && 
                                        String(selectedUserRef.current.id) === String(relevantUserId) && 
                                        isOpenRef.current &&
                                        isActiveRef.current;
      
      if (isViewingThisConversation) {
        const messageContent = data.message || data.messageContent || '';
        const newMessage = {
          id: data.messageId || data.id || `ws-${Date.now()}-${Math.random()}`,
          senderUserId: senderId,
          sender: isIncomingMessage ? 'them' : 'you',
          message: decodeHtmlEntities(messageContent),
          time: formatTime24h(data.timestamp || data.created_at || new Date()),
          timestamp: data.timestamp || data.created_at || new Date().toISOString(),
          attachment: data.attachment || null,
          status: data.status || 'delivered'
        };
        
        if (!mountedRef.current) return;
        
        setChatMessages(prev => {
          const exists = prev.some(msg => 
            msg.id === newMessage.id || 
            (Math.abs(new Date(msg.timestamp) - new Date(newMessage.timestamp)) < 1000 && 
             msg.message === newMessage.message &&
             String(msg.senderUserId) === String(newMessage.senderUserId))
          );
          if (exists) return prev;
          return [...prev, newMessage];
        });
        
        cleanup.setTimeout(() => {
          if (mountedRef.current && messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = 0;
          }
        }, 100);
        
        if (isIncomingMessage) {
          await markMessagesAsReadAndSync(relevantUserId);
        }
      } else {
        const messageContent = data.message || data.messageContent || '';
        
        if (isIncomingMessage) {
          if (!mountedRef.current) return;
          
          let sender = contactsRef.current.find(c => String(c.id) === String(relevantUserId));
          let senderName = sender?.name || data.senderName || data.sender_name || t('videoGrid.someone');
          
          const previewText = messageContent.length > 50 
            ? messageContent.substring(0, 47) + '...' 
            : messageContent;
          
          toast.info(
            <div className="flex flex-col">
              <span className="font-semibold">{senderName}</span>
              <span className="text-sm">{previewText}</span>
            </div>,
            {
              duration: 4000,
              position: 'top-right',
              action: sender ? {
                label: t('personalChat.notification.view'),
                onClick: () => {
                  if (mountedRef.current) {
                    handleUserSelect(sender);
                  }
                }
              } : undefined
            }
          );
          
          if (!mountedRef.current) return;
          
          setContacts(prev => {
            const updated = prev.map(contact => {
              if (String(contact.id) === String(relevantUserId)) {
                return {
                  ...contact,
                  lastMessage: messageContent ? (messageContent.length > 30 
                    ? messageContent.substring(0, 27) + "..." 
                    : messageContent) : contact.lastMessage,
                  lastMessageTime: formatTime24h(data.timestamp || data.created_at || new Date()),
                  unread: (contact.unread || 0) + 1
                };
              }
              return contact;
            });
            return updated;
          });
          
          setFilteredContacts(prev => {
            const updated = prev.map(contact => {
              if (String(contact.id) === String(relevantUserId)) {
                return {
                  ...contact,
                  lastMessage: messageContent ? (messageContent.length > 30 
                    ? messageContent.substring(0, 27) + "..." 
                    : messageContent) : contact.lastMessage,
                  lastMessageTime: formatTime24h(data.timestamp || data.created_at || new Date()),
                  unread: (contact.unread || 0) + 1
                };
              }
              return contact;
            });
            return updated;
          });
        }
      }
    };
    
    const unsubscribeDirect = addMessageHandler('direct_message', (data) => {
      if (!mountedRef.current) return;
      
      const messageData = data.message || {};
      
      const normalizedMessage = {
        messageId: messageData.id,
        senderId: data.senderId,
        receiverId: data.receiverId,
        message: messageData.message || messageData.messageContent || '',
        timestamp: messageData.created_at || data.timestamp,
        attachment: messageData.attachment,
        status: 'delivered',
        senderName: data.senderName || data.sender_name
      };
      
      if (!normalizedMessage.receiverId) {
        const currentUserId = currentUserIdRef.current;
        if (String(normalizedMessage.senderId) !== String(currentUserId)) {
          normalizedMessage.receiverId = currentUserId;
        }
      }
      
      handleChatMessage(normalizedMessage);
    });
    wsUnsubscribersRef.current.push(unsubscribeDirect);
    
    const unsubscribeChat = addMessageHandler('chat_message', handleChatMessage);
    wsUnsubscribersRef.current.push(unsubscribeChat);
    
    const unsubscribeNew = addMessageHandler('new_message', (data) => {
      if (!mountedRef.current) return;
      if (data.chatType === 'normal' || !data.sessionId) {
        handleChatMessage(data);
      }
    });
    wsUnsubscribersRef.current.push(unsubscribeNew);
    
    return () => {
      wsUnsubscribersRef.current.forEach(unsub => {
        try {
          unsub();
        } catch (e) {
          console.debug('Unsubscribe error:', e);
        }
      });
      wsUnsubscribersRef.current = [];
    };
  }, [isConnected, addMessageHandler, markMessagesAsReadAndSync, cleanup, t, playDirectMessage]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredContacts(contacts);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = contacts.filter(contact => 
        contact.name.toLowerCase().includes(query) ||
        (contact.lastMessage && decodeHtmlEntities(contact.lastMessage).toLowerCase().includes(query))
      );
      setFilteredContacts(filtered);
    }
  }, [searchQuery, contacts]);

  const handleFileSelect = useCallback((file) => {
    if (!mountedRef.current) return;
    
    if (file.size > MAX_FILE_SIZE) {
      setSendMessageError(t('personalChat.error.fileTooLargeDetailed', { size: (file.size / 1024 / 1024).toFixed(2) }));
      toast.error(t('personalChat.error.fileTooLarge'));
      return;
    }
    setAttachedFile(file);
    setSendMessageError('');
  }, [MAX_FILE_SIZE, t]);

  const handleFileInputChange = useCallback((e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  }, [handleFileSelect]);

  const removeAttachedFile = useCallback(() => {
    if (!mountedRef.current) return;
    
    setAttachedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handlePaste = useCallback((e) => {
    if (!mountedRef.current) return;
    const items = e.clipboardData?.items;
    if (!items) return;
    
    for (const item of items) {
      if (item.kind === 'file') {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;
        if (file.size > MAX_FILE_SIZE) {
          toast.error(t('personalChat.error.fileTooLarge'));
          return;
        }
        setAttachedFile(file);
        return;
      }
    }
  }, [MAX_FILE_SIZE, t]);

  const handleUserSelect = useCallback((user) => {
    if (!user || !mountedRef.current) return;
    if (selectedUser?.id === user.id && !showUserList) return;

    setSelectedUser(user);
    setChatMessages([]);
    setMessagesError('');
    setSendMessageError('');
    setShowUserList(false);
    setHasMoreMessages(false);
    setOldestMessageId(null);
  }, [selectedUser, showUserList]);

  useEffect(() => {
    if (!isOpen) return;
    
    const userId = currentUser?.id || currentUser?.userId;
    if (!userId) return;
    
    if (hasLoadedContactsRef.current) return;

    const fetchContacts = async () => {
      if (!mountedRef.current) return;
      
      setIsLoadingContacts(true);
      setContactsError('');
      
      try {
        const response = await getChatContacts();
        
        if (!mountedRef.current) return;
        
        if (response.success && Array.isArray(response.data)) {
          const contactsWithDecodedMessages = response.data.map(contact => ({
            ...contact,
            lastMessage: contact.lastMessage ? decodeHtmlEntities(contact.lastMessage) : contact.lastMessage
          }));
          setContacts(contactsWithDecodedMessages);
          setFilteredContacts(contactsWithDecodedMessages);
          hasLoadedContactsRef.current = true;
        } else {
          setContactsError(response.message || t('personalChat.error.loadContactsFailed'));
        }
      } catch (err) {
        if (mountedRef.current) {
          setContactsError(err.message || t('personalChat.error.loadContactsError'));
        }
      } finally {
        if (mountedRef.current) {
          setIsLoadingContacts(false);
        }
      }
    };

    fetchContacts();
  }, [isOpen, currentUser, t]);

  useEffect(() => {
    if (!isOpen) {
      setShowUserList(true);
      hasLoadedContactsRef.current = false;
      
      cleanup.setTimeout(() => {
        if (!isOpenRef.current && mountedRef.current) {
          setContacts([]);
          setFilteredContacts([]);
          setSelectedUser(null);
          setChatMessages([]);
          setMessageInput('');
          setSendMessageError('');
          setSearchQuery('');
          removeAttachedFile();
          setHasMoreMessages(false);
          setOldestMessageId(null);
        }
      }, 300);
    }
  }, [isOpen, cleanup, removeAttachedFile]);

  useEffect(() => {
    if (!selectedUser || !isOpen || showUserList) {
      if (!selectedUser) {
        setChatMessages([]);
        setHasMoreMessages(false);
        setOldestMessageId(null);
      }
      return;
    }

    const userId = currentUserIdRef.current;
    if (!userId) return;

    const fetchMessages = async () => {
      if (!mountedRef.current) return;
      
      setIsLoadingMessages(true);
      setMessagesError('');
      
      try {
        const response = await getChatMessages(selectedUser.id, MESSAGES_PER_PAGE);
        
        if (!mountedRef.current) return;
        
        if (response.success && Array.isArray(response.data)) {
          const messages = response.data;
          
          const messagesWithFormattedTime = messages.map(msg => ({
            ...msg,
            message: msg.message || msg.messageContent || '',
            time: formatTime24h(msg.timestamp || msg.sent_at)
          }));
          
          setChatMessages(messagesWithFormattedTime);
          setHasMoreMessages(response.hasMore || messages.length === MESSAGES_PER_PAGE);
          
          if (messagesWithFormattedTime.length > 0) {
            const oldestMsg = messagesWithFormattedTime[messagesWithFormattedTime.length - 1];
            setOldestMessageId(oldestMsg.id);
          }

          if (isActiveRef.current) {
            const contactIndex = contacts.findIndex(c => c.id === selectedUser.id);
            if (contactIndex !== -1 && contacts[contactIndex].unread > 0) {
              await markMessagesAsReadAndSync(selectedUser.id);
              if (!mountedRef.current) return;
            }
          }
        } else {
          setMessagesError(response.message || t('personalChat.error.loadMessagesFailed'));
        }
      } catch (err) {
        if (mountedRef.current) {
          setMessagesError(err.message || t('personalChat.error.loadMessagesError'));
        }
      } finally {
        if (mountedRef.current) {
          setIsLoadingMessages(false);
          cleanup.setTimeout(() => scrollToBottom(true), 100);
        }
      }
    };

    fetchMessages();
  }, [selectedUser?.id, isOpen, showUserList, isActive, contacts, markMessagesAsReadAndSync, scrollToBottom, cleanup, t]);

  const loadMoreMessages = useCallback(async () => {
    if (isLoadingMoreMessages || !hasMoreMessages || !selectedUser || !oldestMessageId || !mountedRef.current) return;
    
    isLoadingMoreRef.current = true;
    setIsLoadingMoreMessages(true);
    
    try {
      const container = messagesContainerRef.current;
      if (!container) return;
      
      const scrollHeightBefore = container.scrollHeight;
      const scrollTopBefore = container.scrollTop;
      
      const response = await getChatMessages(selectedUser.id, MESSAGES_PER_PAGE, oldestMessageId);
      
      if (!mountedRef.current) return;
      
      if (response.success && Array.isArray(response.data)) {
        const messages = response.data;
        
        if (messages.length === 0) {
          setHasMoreMessages(false);
          return;
        }
        
        const messagesWithFormattedTime = messages.map(msg => ({
          ...msg,
          message: msg.message || msg.messageContent || '',
          time: formatTime24h(msg.timestamp || msg.sent_at)
        }));
        
        setChatMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newMessages = messagesWithFormattedTime.filter(m => !existingIds.has(m.id));
          
          if (newMessages.length === 0) {
            setHasMoreMessages(false);
            return prev;
          }
          
          return [...newMessages, ...prev];
        });
        
        setHasMoreMessages(response.hasMore || messages.length === MESSAGES_PER_PAGE);
        
        if (messagesWithFormattedTime.length > 0) {
          const oldestNewMsg = messagesWithFormattedTime[messagesWithFormattedTime.length - 1];
          setOldestMessageId(oldestNewMsg.id);
        }
        
        requestAnimationFrame(() => {
          if (container && mountedRef.current) {
            const scrollHeightAfter = container.scrollHeight;
            const addedHeight = scrollHeightAfter - scrollHeightBefore;
            container.scrollTop = scrollTopBefore + addedHeight;
          }
        });
      }
    } catch (err) {
      if (mountedRef.current) {
        console.error('Failed to load more messages:', err);
        toast.error(t('personalChat.error.loadMoreFailed'));
      }
    } finally {
      if (mountedRef.current) {
        setIsLoadingMoreMessages(false);
        setTimeout(() => {
          isLoadingMoreRef.current = false;
        }, 500);
      }
    }
  }, [isLoadingMoreMessages, hasMoreMessages, selectedUser, oldestMessageId, t]);

  useEffect(() => {
    if (isActive && selectedUser && !showUserList) {
      const markAsRead = async () => {
        const contact = contacts.find(c => c.id === selectedUser.id);
        if (contact && contact.unread > 0) {
          await markMessagesAsReadAndSync(selectedUser.id);
        }
      };
      
      markAsRead();
    }
  }, [isActive, selectedUser, showUserList, contacts, markMessagesAsReadAndSync]);

  const handleBackToUsers = useCallback(() => {
    if (!mountedRef.current) return;
    setShowUserList(true);
    setSelectedUser(null);
  }, []);

  const handleSendMessage = useCallback(async () => {
    if ((!messageInput.trim() && !attachedFile) || !selectedUser || !mountedRef.current) return;

    const userId = currentUserIdRef.current;
    if (!userId) return;

    setIsSendingMessage(true);
    setSendMessageError('');
    
    let attachmentData = null;
    const originalFileName = attachedFile?.name;
    
    if (attachedFile) {
      setIsUploadingFile(true);
      try {
        const formData = new FormData();
        formData.append('file', attachedFile);
        formData.append('receiverId', selectedUser.id);
        
        const uploadResponse = await uploadChatAttachment(formData, (progress) => {
          if (mountedRef.current) {
            setUploadProgress(progress);
          }
        });
        
        if (!mountedRef.current) return;
        
        if (uploadResponse.success) {
          let fileType = uploadResponse.data.fileType || attachedFile.type;
          
          const typeMapping = {
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'application/docx',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'application/xlsx',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'application/pptx',
            'application/vnd.ms-excel': 'application/excel',
            'application/vnd.ms-powerpoint': 'application/ppt',
            'application/vnd.ms-word': 'application/word',
            'application/vnd.oasis.opendocument.text': 'application/odt',
            'application/vnd.oasis.opendocument.spreadsheet': 'application/ods',
            'application/vnd.oasis.opendocument.presentation': 'application/odp'
          };
          
          fileType = typeMapping[fileType] || fileType;
          if (fileType.length > 50) {
            fileType = fileType.substring(0, 50);
          }
          
          attachmentData = {
            attachmentName: originalFileName,
            attachmentType: fileType,
            attachmentSizeKb: Math.round(uploadResponse.data.fileSize / 1024),
            attachmentUrl: uploadResponse.data.fileUrl
          };
        } else {
          if (mountedRef.current) {
            setSendMessageError(uploadResponse.message || t('personalChat.error.uploadFailed'));
            setIsSendingMessage(false);
            setIsUploadingFile(false);
          }
          return;
        }
      } catch (uploadError) {
        if (mountedRef.current) {
          setSendMessageError(t('personalChat.error.uploadError'));
          setIsSendingMessage(false);
          setIsUploadingFile(false);
        }
        return;
      } finally {
        if (mountedRef.current) {
          setIsUploadingFile(false);
          setUploadProgress(0);
        }
      }
    }
    
    if (!mountedRef.current) return;
    
    const tempMessageId = `temp-${Date.now()}`;
    const optimisticMessage = {
      id: tempMessageId,
      senderUserId: userId,
      sender: 'you',
      message: messageInput,
      time: formatTime24h(new Date()),
      timestamp: new Date().toISOString(),
      status: 'sending',
      attachment: attachedFile ? {
        name: originalFileName,
        type: attachedFile.type,
        size: `${(attachedFile.size / 1024).toFixed(0)} KB`,
        url: attachmentData?.attachmentUrl
      } : null
    };

    setChatMessages(prevMessages => [...prevMessages, optimisticMessage]);
    scrollToBottom(true);
    const messageToSend = messageInput;
    setMessageInput('');
    removeAttachedFile();
    
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      const messageData = {
        message: messageToSend,
        ...attachmentData
      };
      
      const response = await sendChatMessage(selectedUser.id, messageData);
      
      if (!mountedRef.current) return;
      
      if (response.success && response.data) {
        setChatMessages(prevMessages =>
          prevMessages.map(msg => 
            msg.id === tempMessageId 
              ? { 
                  ...response.data, 
                  message: response.data.message || response.data.messageContent || messageToSend,
                  sender: 'you',
                  status: response.data.status || 'delivered',
                  time: formatTime24h(response.data.timestamp || response.data.sent_at),
                  attachment: response.data.attachment ? {
                    ...response.data.attachment,
                    name: originalFileName || response.data.attachment.name
                  } : null
                }
              : msg
          )
        );
        
        setContacts(prevContacts => {
          const updatedContacts = prevContacts.map(c =>
            c.id === selectedUser.id ? {
                ...c,
                lastMessage: messageToSend.length > 30 
                  ? messageToSend.substring(0, 27) + "..." 
                  : messageToSend,
                lastMessageTime: formatTime24h(new Date()),
            } : c
          );
          return updatedContacts;
        });
        
        setFilteredContacts(prevContacts => {
          const updatedContacts = prevContacts.map(c =>
            c.id === selectedUser.id ? {
                ...c,
                lastMessage: messageToSend.length > 30 
                  ? messageToSend.substring(0, 27) + "..." 
                  : messageToSend,
                lastMessageTime: formatTime24h(new Date()),
            } : c
          );
          return updatedContacts;
        });
        
      } else {
        if (mountedRef.current) {
          setSendMessageError(response.message || t('personalChat.error.sendFailed'));
          setChatMessages(prevMessages => prevMessages.filter(msg => msg.id !== tempMessageId));
          setMessageInput(messageToSend);
        }
        
        if (attachmentData?.attachmentUrl) {
          try {
            await deleteChatAttachment(attachmentData.attachmentUrl);
          } catch (deleteError) {
            // Silent fail
          }
        }
      }
    } catch (err) {
      if (mountedRef.current) {
        setSendMessageError(err.message || t('personalChat.error.sendError'));
        setChatMessages(prevMessages => prevMessages.filter(msg => msg.id !== tempMessageId));
        setMessageInput(messageToSend);
      }
    } finally {
      if (mountedRef.current) {
        setIsSendingMessage(false);
        requestAnimationFrame(() => {
          if (textareaRef.current) textareaRef.current.focus();
        });
      }
    }
  }, [messageInput, attachedFile, selectedUser, removeAttachedFile, scrollToBottom, t]);

  const handleMessageInputChange = useCallback((e) => {
    const newValue = e.target.value;
    if (newValue.length <= MESSAGE_CHAR_LIMIT && mountedRef.current) {
      setMessageInput(newValue);
      
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
      }
    }
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isSendingMessage && !isUploadingFile) {
        handleSendMessage();
      }
    }
  }, [isSendingMessage, isUploadingFile, handleSendMessage]);

  const handleAttachmentClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      cleanup.cleanup();
      wsUnsubscribersRef.current.forEach(unsub => {
        try {
          unsub();
        } catch (e) {
          console.debug('Final cleanup error:', e);
        }
      });
      wsUnsubscribersRef.current = [];
      hasLoadedContactsRef.current = false;
    };
  }, [cleanup]);

  // ✅ CRITICAL FIX: Proper cleanup when component unmounts with active chat
  useEffect(() => {
    return () => {
      // On unmount, if we have a selected user, mark their messages as read globally
      if (selectedUserRef.current && mountedRef.current) {
        const userId = selectedUserRef.current.id;
        console.log('PersonalChatInSession unmounting with active user:', userId);
        
        // FIRST: Mark as read globally BEFORE clearing tracking
        if (window.markMessagesReadGlobally) {
          window.markMessagesReadGlobally(userId);
        }
        
        // THEN: Clear from session tracking
        if (window.clearUserFromSessionTracking) {
          window.clearUserFromSessionTracking(userId);
        }
      }
    };
  }, []); // Empty deps - only runs on unmount

  const renderMessagesWithDates = () => {
    const elements = [];
    let lastDateKey = null;

    chatMessages.forEach((msg, index) => {
      const currentDateKey = getDateKey(msg.timestamp);
      
      if (currentDateKey !== lastDateKey) {
        elements.push(
          <div key={`date-${currentDateKey}`} className="flex items-center justify-center my-4">
            <div className="bg-gray-700 px-3 py-1 rounded-full">
              <span className="text-xs font-medium text-gray-300">
                {formatDateSeparator(msg.timestamp, t)}
              </span>
            </div>
          </div>
        );
        lastDateKey = currentDateKey;
      }

      elements.push(
        <div 
          key={msg.id} 
          className={`flex mb-2.5 ${msg.sender === 'you' ? 'justify-end' : 'justify-start'}`}
        >
          <div className={`max-w-[75%] min-w-0 px-3 py-2 rounded-lg ${
            msg.sender === 'you' 
              ? 'bg-cyan-600 text-white' 
              : 'bg-gray-700 text-white'
          } ${msg.status === 'sending' ? 'opacity-70' : ''}`}>
            {msg.attachment && (
              <div className={`mb-2 p-2 rounded flex items-center gap-2 ${
                msg.sender === 'you' ? 'bg-cyan-700' : 'bg-gray-600'
              }`}>
                <DocumentTextIcon className={`w-5 h-5 flex-shrink-0 ${
                  msg.sender === 'you' ? 'text-cyan-100' : 'text-gray-300'
                }`} />
                <div className="text-xs flex-1 min-w-0">
                  <p className="font-medium truncate text-white">
                    {msg.attachment.name}
                  </p>
                  <p className={msg.sender === 'you' ? 'text-cyan-100' : 'text-gray-300'}>
                    {msg.attachment.size}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => downloadFile(msg.attachment.url, msg.attachment.name)}
                  className={`p-1.5 rounded flex-shrink-0 transition-colors ${
                    msg.sender === 'you' ? 'hover:bg-cyan-600 text-cyan-100' : 'hover:bg-gray-500 text-gray-300'
                  }`}
                  title="Download"
                  aria-label="Download"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className="text-sm whitespace-pre-wrap break-words leading-relaxed">
              {formatMessageWithLinks(msg.message, msg.sender === 'you')}
            </div>
            <p className={`text-xs mt-1 ${
              msg.sender === 'you' ? 'text-cyan-200 text-right' : 'text-gray-400'
            }`}>
              {msg.time}
            </p>
          </div>
        </div>
      );
    });

    return elements;
  };

  const renderMessagesWithDatesReversed = () => {
    const elements = [];
    const reversedMessages = [...chatMessages].reverse();

    reversedMessages.forEach((msg, index) => {
      const currentDateKey = getDateKey(msg.timestamp);

      // message first — column-reverse puts it below what comes after
      elements.push(
        <div key={msg.id} className={`flex mb-2.5 ${msg.sender === 'you' ? 'justify-end' : 'justify-start'}`}>
          <div className={`max-w-[75%] min-w-0 px-3 py-2 rounded-lg ${
            msg.sender === 'you' ? 'bg-cyan-600 text-white' : 'bg-gray-700 text-white'
          } ${msg.status === 'sending' ? 'opacity-70' : ''}`}>
            {msg.attachment && (
              <div className={`mb-2 p-2 rounded flex items-center gap-2 ${
                msg.sender === 'you' ? 'bg-cyan-700' : 'bg-gray-600'
              }`}>
                <DocumentTextIcon className={`w-5 h-5 flex-shrink-0 ${
                  msg.sender === 'you' ? 'text-cyan-100' : 'text-gray-300'
                }`} />
                <div className="text-xs flex-1 min-w-0">
                  <p className="font-medium truncate text-white">{msg.attachment.name}</p>
                  <p className={msg.sender === 'you' ? 'text-cyan-100' : 'text-gray-300'}>{msg.attachment.size}</p>
                </div>
                <button
                  type="button"
                  onClick={() => downloadFile(msg.attachment.url, msg.attachment.name)}
                  className={`p-1.5 rounded flex-shrink-0 transition-colors ${
                    msg.sender === 'you' ? 'hover:bg-cyan-600 text-cyan-100' : 'hover:bg-gray-500 text-gray-300'
                  }`}
                  title="Download"
                  aria-label="Download"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className="text-sm whitespace-pre-wrap break-words leading-relaxed">
              {formatMessageWithLinks(msg.message, msg.sender === 'you')}
            </div>
            <p className={`text-xs mt-1 ${msg.sender === 'you' ? 'text-cyan-200 text-right' : 'text-gray-400'}`}>
              {msg.time}
            </p>
          </div>
        </div>
      );

      // Push date separator after the OLDEST message of each date group.
      // In column-reverse this places it visually ABOVE all messages for that day.
      const nextMsg = reversedMessages[index + 1];
      const nextDateKey = nextMsg ? getDateKey(nextMsg.timestamp) : null;

      if (currentDateKey !== nextDateKey) {
        elements.push(
          <div key={`date-${currentDateKey}`} className="flex items-center justify-center my-4">
            <div className="bg-gray-700 px-3 py-1 rounded-full">
              <span className="text-xs font-medium text-gray-300">
                {formatDateSeparator(msg.timestamp, t)}
              </span>
            </div>
          </div>
        );
      }
    });

    return elements;
  };

  if (!isOpen) return null;

  return (
    <>
      <style jsx global>{`
        .personal-chat-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        
        .personal-chat-scrollbar::-webkit-scrollbar-track {
          background: rgba(31, 41, 55, 0.3);
        }
        
        .personal-chat-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(75, 85, 99, 0.5);
          border-radius: 3px;
        }
        
        .personal-chat-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(107, 114, 128, 0.7);
        }
        
        .personal-chat-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(75, 85, 99, 0.5) rgba(31, 41, 55, 0.3);
        }
        
        .chat-textarea {
          resize: none;
          overflow-y: auto;
          max-height: 120px;
        }
        
        .drag-overlay-active {
          animation: pulse-border 1.5s ease-in-out infinite;
        }
        
        @keyframes pulse-border {
          0%, 100% {
            border-color: rgb(6, 182, 212);
            opacity: 1;
          }
          50% {
            border-color: rgb(8, 145, 178);
            opacity: 0.8;
          }
        }
      `}</style>
      
      <div 
        className="flex h-full overflow-hidden bg-gray-800"
        data-viewing-user-id={!showUserList && selectedUser ? selectedUser.id : null}
        data-session-chat="personal"
      >
        {showUserList ? (
          <div className="flex flex-col w-full">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
              <h3 className="text-base font-semibold text-white">{t('chat.personal')}</h3>
              <button
                onClick={onClose}
                className="p-1 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="px-3 py-2.5 border-b border-gray-700">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('personalChat.searchPlaceholder')}
                  className="w-full pl-9 pr-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto personal-chat-scrollbar">
              {isLoadingContacts && (
                <div className="p-4 text-center">
                  <div className="inline-block w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-sm text-gray-400 mt-2">{t('personalChat.loadingContacts')}</p>
                </div>
              )}
              
              {contactsError && (
                <div className="p-4">
                  <p className="text-sm text-red-400">{contactsError}</p>
                </div>
              )}
              
              {!isLoadingContacts && !contactsError && filteredContacts.length === 0 && searchQuery && (
                <div className="p-4 text-center">
                  <p className="text-sm text-gray-400">{t('personalChat.noContactsFound')}</p>
                </div>
              )}
              
              {!isLoadingContacts && !contactsError && contacts.length === 0 && !searchQuery && (
                <div className="p-4 text-center">
                  <p className="text-sm text-gray-400">{t('personalChat.noContacts')}</p>
                </div>
              )}
              
              {filteredContacts.map((contact) => (
                <button
                  key={contact.id}
                  onClick={() => handleUserSelect(contact)}
                  className="w-full px-4 py-2.5 flex items-center hover:bg-gray-700 transition-colors border-b border-gray-700 text-left"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-white truncate">{contact.name}</p>
                      {contact.unread > 0 && (
                        <div className="bg-cyan-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center ml-2 flex-shrink-0">
                          {contact.unread}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col w-full">
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-700 bg-gray-750">
              <div className="flex items-center min-w-0 flex-1">
                <button
                  onClick={handleBackToUsers}
                  className="p-1 mr-1.5 text-gray-400 hover:text-white rounded transition-colors flex-shrink-0"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>
                <h3 className="text-sm font-medium text-white truncate">{selectedUser?.name}</h3>
              </div>
              <button
                onClick={onClose}
                className="p-1 text-gray-400 hover:text-white rounded transition-colors flex-shrink-0 ml-2"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div 
              ref={dropZoneRef}
              className="flex-1 flex flex-col overflow-hidden relative min-h-0"
              data-drop-zone="wrapper"
            >
              {isDragging && (
                <div className="absolute inset-0 bg-gray-900 bg-opacity-95 flex items-center justify-center z-50 border-4 border-dashed drag-overlay-active rounded-lg pointer-events-none">
                  <div className="text-center">
                    <ArrowUpTrayIcon className="w-16 h-16 text-cyan-400 mx-auto mb-4 animate-bounce" />
                    <p className="text-2xl font-bold text-white mb-2">{t('personalChat.dropZone.title')}</p>
                    <p className="text-lg text-cyan-300">{t('personalChat.dropZone.subtitle')}</p>
                    <p className="text-sm text-gray-400 mt-2">{t('personalChat.dropZone.maxSize')}</p>
                  </div>
                </div>
              )}

              <div 
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto px-4 py-3 personal-chat-scrollbar min-h-0"
                style={{ display: 'flex', flexDirection: 'column-reverse' }}
              >
                {/* column-reverse: scrollTop 0 = visual bottom, new messages appear there naturally */}
                <div ref={messagesEndRef} />
                {!isLoadingMessages && chatMessages.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-400">{t('personalChat.noMessages')}</p>
                  </div>
                )}
                {messagesError && (
                  <div className="text-center py-8">
                    <p className="text-sm text-red-400">{messagesError}</p>
                  </div>
                )}
                {isLoadingMessages && (
                  <div className="text-center py-8">
                    <div className="inline-block w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-sm text-gray-400 mt-2">{t('personalChat.loadingMessages')}</p>
                  </div>
                )}
                {renderMessagesWithDatesReversed()}
                {hasMoreMessages && (
                  <div className="text-center py-2">
                    {isLoadingMoreMessages ? (
                      <div className="inline-block w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <button
                        onClick={loadMoreMessages}
                        className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                      >
                        {t('personalChat.loadMore')}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {sendMessageError && (
                <div className="px-3 pb-1">
                  <p className="text-xs text-red-400">{sendMessageError}</p>
                </div>
              )}

              {attachedFile && (
                <div className="mx-3 mb-2 p-2 bg-gray-700 rounded flex items-center">
                  <DocumentTextIcon className="w-5 h-5 text-gray-300 flex-shrink-0" />
                  <div className="flex-1 min-w-0 ml-2">
                    <p className="text-xs font-medium text-white truncate">{attachedFile.name}</p>
                    <p className="text-xs text-gray-400">{(attachedFile.size / 1024).toFixed(0)} KB</p>
                  </div>
                  <button
                    onClick={removeAttachedFile}
                    className="ml-2 p-0.5 text-gray-400 hover:text-red-400 transition-colors flex-shrink-0"
                  >
                    <XCircleIcon className="w-4 h-4" />
                  </button>
                </div>
              )}

              {isUploadingFile && (
                <div className="mx-3 mb-2">
                  <div className="bg-gray-700 rounded-full h-1.5">
                    <div 
                      className="bg-cyan-500 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{t('chat.uploading', { progress: uploadProgress })}</p>
                </div>
              )}

              <div className="px-3 py-2 border-t border-gray-700 bg-gray-750">
                <div className="flex items-end gap-2">
                  <button
                    onClick={handleAttachmentClick}
                    className="p-1.5 text-gray-400 hover:text-white rounded hover:bg-gray-700 transition-colors flex-shrink-0"
                    disabled={isSendingMessage || isUploadingFile}
                    title={t('chat.attachFile')}
                  >
                    <PaperClipIcon className="w-4 h-4" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileInputChange}
                    accept="*/*"
                  />
                  
                  <textarea
                    ref={textareaRef}
                    value={messageInput}
                    onChange={handleMessageInputChange}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    placeholder={t('chat.inputPlaceholder')}
                    className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 chat-textarea"
                    disabled={isSendingMessage || isUploadingFile}
                    rows={1}
                    style={{ minHeight: '38px' }}
                  />
                  
                  <button
                    onClick={handleSendMessage}
                    disabled={(!messageInput.trim() && !attachedFile) || isSendingMessage || isUploadingFile}
                    className={`p-2 rounded-lg transition-all flex-shrink-0 ${
                      (messageInput.trim() || attachedFile) && !isSendingMessage && !isUploadingFile
                        ? 'bg-cyan-600 hover:bg-cyan-700 text-white shadow-sm'
                        : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    }`}
                    title={t('chat.sendMessage')}
                  >
                    <ArrowUpIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default React.memo(PersonalChatInSession);